// The home page's cards (byj-1wv.137): Share's features in the guide's
// groups, each the place it opens, the guide's section that says more, and
// the main server's module it needs — a card for a module the deployment
// does not serve is not shown, as its place is not in the sidebar. What a
// card says is home.json's, under `cards.<id>`.
import type { User } from "./api";

export type HomeGroup = "sharing" | "classes" | "make" | "plans";

export type HomeIcon = "shares" | "record" | "terminal" | "classes" | "exams" | "agents" | "templates" | "files" | "machines" | "billing";

export interface HomeCard {
  id: "share" | "record" | "agentShares" | "classes" | "exams" | "agents" | "templates" | "files" | "machines" | "plans";
  group: HomeGroup;
  icon: HomeIcon;
  /** the page it opens */
  to: string;
  /** the guide's section, /docs#…; none where the guide has none */
  doc?: string;
  /** the module it needs (RFC-059 §6) */
  module?: string;
}

export const homeGroups: HomeGroup[] = ["sharing", "classes", "make", "plans"];

export const homeCards: HomeCard[] = [
  { id: "share", group: "sharing", icon: "shares", to: "/shares", doc: "share", module: "live" },
  { id: "record", group: "sharing", icon: "record", to: "/shares", doc: "record", module: "live" },
  { id: "agentShares", group: "sharing", icon: "terminal", to: "/shares", doc: "agents", module: "live" },
  { id: "classes", group: "classes", icon: "classes", to: "/classes", doc: "classes", module: "classes" },
  { id: "exams", group: "classes", icon: "exams", to: "/exams", doc: "exams", module: "exams" },
  { id: "agents", group: "make", icon: "agents", to: "/agents", module: "agents" },
  { id: "templates", group: "make", icon: "templates", to: "/templates", doc: "templates", module: "app-templates" },
  { id: "files", group: "make", icon: "files", to: "/files", doc: "files", module: "files" },
  { id: "machines", group: "make", icon: "machines", to: "/machines", doc: "machines", module: "machines" },
  { id: "plans", group: "plans", icon: "billing", to: "/billing", doc: "plans", module: "billing" },
];

/** Whether the main server serves `module`: every one when it names none. */
export const serves = (user: Pick<User, "modules">, module?: string) => !module || !user.modules || user.modules.includes(module);

/** The cards a person is shown, in their groups' order, each group with any. */
export function cardsFor(user: Pick<User, "modules">): { group: HomeGroup; cards: HomeCard[] }[] {
  return homeGroups.map((group) => ({ group, cards: homeCards.filter((c) => c.group === group && serves(user, c.module)) })).filter((g) => g.cards.length > 0);
}

/**
 * Where a sign-in lands: the home page, for a person of Share's own; a
 * third party's people keep the page they landed on before it, their own.
 */
export const landing = (user: Pick<User, "integration">) => (user.integration ? "/shares" : "/home");

/** The quick start's commands, each step's: install, sign this computer in, share a session. */
export const quickStart = {
  install: ["brew tap grumatic/tap", "brew install grumatic/tap/byj grumatic/tap/byj-share"],
  signIn: ["byj-share login"],
  share: ["byj run --share link"],
};
